import axios, { AxiosInstance } from "axios";
import { UserDetails } from "../types/authentication.ts";
import { EmailListFilters, EmailListPage, EmailListUploadDTO, EmailListUploadSummary } from "../types/emailList.ts";
import { MarketingCampaignFilters, MarketingCampaignPage, NewMarketingCampaign } from "../types/marketing-campaign.ts";
import { SourceCode, Team } from "../types/metadata.ts";
import { BullseyeQuery, RunQueryResponse } from "../types/query.ts";

const BASE_URL = import.meta.env.VITE_SERVICES_URL;

export const AuthenticatedClient: AxiosInstance = axios.create({
  baseURL: `${BASE_URL}/api`
});

// Configure the client to use Bearer token when authenticated
AuthenticatedClient.interceptors.request.use(config => {
  const token = localStorage.getItem("okta-token-storage");

  if (token) {
    const { accessToken = {}, idToken = {} } = JSON.parse(token);

    config.headers.Authorization = `Bearer ${accessToken.accessToken}`;
    config.headers["x-id-token"] = idToken.idToken;
  }

  return config;
});

// Logout prospect if token is invalid
AuthenticatedClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.clear();
      window.location.reload();
    }

    return Promise.reject(error);
  }
);

export const getUserInfo = async (): Promise<UserDetails> => (await AuthenticatedClient.get(`/user/info`)).data.entity;

export const searchEmailLists = async (
  filters: EmailListFilters,
  pageNumber: number,
  pageSize: number
): Promise<EmailListPage> =>
  (await AuthenticatedClient.post(`/email-lists?page=${pageNumber}&size=${pageSize}`, filters)).data;

export const uploadEmailList = async (body: EmailListUploadDTO): Promise<void> =>
  await AuthenticatedClient.post("/email-lists/upload", body);

export const getEmailListSummary = async (emailListId: number): Promise<EmailListUploadSummary> =>
  (await AuthenticatedClient.get(`/email-lists/${emailListId}/summary`)).data;

export const downloadEmailListSummaryExport = async (emailListId: number): Promise<any> =>
  await AuthenticatedClient.get(`/email-lists/${emailListId}/summary/export`, {
    responseType: "blob"
  });

export const searchMarketingCampaigns = async (
  filters: MarketingCampaignFilters,
  pageNumber: number,
  pageSize: number
): Promise<MarketingCampaignPage> =>
  (await AuthenticatedClient.post(`/marketing-campaigns/search?page=${pageNumber}&size=${pageSize}`, filters)).data;

export const createMarketingCampaign = async (body: NewMarketingCampaign): Promise<void> =>
  (await AuthenticatedClient.post("/marketing-campaigns", body)).data;

export const getTeams = async (): Promise<Team[]> => (await AuthenticatedClient.get("/metadata/teams")).data;

export const getSourceCodes = async (): Promise<SourceCode[]> =>
  (await AuthenticatedClient.get("/metadata/source-codes")).data;

export const getQueries = async (): Promise<BullseyeQuery[]> => (await AuthenticatedClient.get("/queries")).data;

export const runQuery = async (query: string): Promise<RunQueryResponse> =>
  (await AuthenticatedClient.post("/queries/run", { query })).data;
